<template>
  <Loader
    :value="promises.fetch"
    :message="$t('loading', { resource: $tc('product', 2).toLowerCase() }) + '...'"
    class="products"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #actions>
          <Actions placement="products" />
          <DropdownComponent :closeOnClick="false">
            <ButtonComponent :label="$t('add')" :icon="['fal', 'plus']" theme="primary" />
            <template #dropdown="{ close, visible }">
              <ButtonComponent
                tag="router-link"
                :label="$tc('product')"
                :to="{ name: 'product-create', query: { returnTo: $route.path } }"
                :icon="['fal', 'plus']"
                @click="close"
              />
              <ButtonComponent
                tag="router-link"
                :label="$tc('import')"
                :to="{ name: 'import', params: { type: 'product' } }"
                :icon="['fal', 'file-import']"
                @click="close"
              />
              <ButtonComponent
                v-if="catalogs.length"
                :label="$tc('importFromCatalog')"
                :icon="['fal', 'book-open']"
                @click="showProductSelect = true"
              />
              <Actions v-if="visible" placement="products:add" type="list" />
            </template>
          </DropdownComponent>
        </template>
      </HeaderComponent>

      <FilterComponent
        v-model="filter"
        :default="defaultFilter"
        :items="['search', 'tags']"
        :options="{
          search: { placeholder: [this.$t('name'), this.$t('internalId')] },
          tags: { items: '/api/product/tags' },
        }"
      />
      <div v-for="(products, category) in categories" :key="category" class="products__category mt-4">
        <label class="products__category-label">
          <ProductCategory :value="category" />
        </label>
        <div class="products__category-content">
          <ProductCard
            v-for="item in products"
            :key="item.id"
            :value="item"
            tag="router-link"
            :to="{ name: 'product-edit', params: { id: item.id } }"
          >
            <template #badges>
              <Badge
                v-if="getCatalog(item)"
                :value="getCatalog(item).label"
                :icon="['fal', 'book-open']"
                theme="primary"
              />
            </template>
          </ProductCard>

          <router-link
            :to="{ name: 'product-create', query: { returnTo: $route.path, category } }"
            tag="div"
            class="products__category-create-product"
          >
            <FontAwesomeIcon :icon="['fal', 'plus']" size="2x" />
            <div>{{ $t('createProduct') }}</div>
          </router-link>
        </div>
      </div>
      <EmptyState
        v-if="!items.length && !states.fetch && !errors.fetch"
        :title="$t('noX', { x: this.$tc('product', 2).toLowerCase() })"
        :subtitle="hasFilters ? this.$t('filterEmptyState') : undefined"
        :icon="['fal', 'shopping-cart']"
        :action-portal="!hasFilters"
      />
    </div>
    <Catalog :show.sync="showProductSelect" :value="catalogs" @refetch="fetch" />
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Debounce, SortByKey } from '@/utils'
import fetch from '@/fetch'

import HeaderComponent from '@/components/Header'
import FilterMixin from '@/components/Filter'
import ProductCard from '@/components/Product/Card'
import ProductCategory from '@/components/VirtualCategory/Input'
import Actions from '@/components/Actions'
import Card from '@/components/Card'
import Badge from '@/components/Badge'
import { DropdownComponent } from 'vue-elder-dropdown'
import Catalog from '@/components/Product/Catalog'
import EmptyState from '@/components/EmptyState'
import tracker from '@/tracking'

export default {
  mixins: [FilterMixin({ default: { search: '', tags: [] } })],
  data() {
    return {
      showProductSelect: null,
      catalogs: [],
    }
  },
  watch: {
    filter: {
      handler: 'fetch',
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapState('Product', ['items', 'promises', 'states', 'errors']),
    categories() {
      return this.items.sort(SortByKey('category')).reduce((res, cur) => {
        if (!(cur.category in res)) res[cur.category] = []
        res[cur.category].push(cur)
        return res
      }, {})
    },
    breadcrumbs() {
      return [{ title: this.$metaInfo.title }]
    },
  },
  methods: {
    ...mapActions('Product', {
      fetch: Debounce(function (dispatch) {
        if (Object.values(this['filter']).filter(v => (Array.isArray(v) ? v.length : Boolean(v))).length)
          tracker.emit(`Product:filter`, { ...this['filter'] })

        this.fetchCatalogs()
        return dispatch('fetch', { filter: this.filter })
      }, 200),
    }),
    fetchCatalogs() {
      this.promise = fetch(`/api/product/catalogs`)
        .then(res => res.json())
        .then(data => (this.catalogs = data))
    },
    getCatalog(item) {
      if (!item.source) return
      return this.catalogs.find(c => c.source === item.source.host)
    },
  },
  metaInfo() {
    return {
      title: this.$tc('product', 2),
    }
  },
  components: {
    ProductCard,
    Card,
    HeaderComponent,
    ProductCategory,
    DropdownComponent,
    Actions,
    Catalog,
    Badge,
    EmptyState,
  },
}
</script>

<style lang="scss">
.products {
  background-color: $background-grey;

  &__category {
    &-label {
      font-size: 1.25rem;
      color: css-lighten('dark', 35%);
      display: block;
      font-weight: normal;
      margin-bottom: 1.5rem;

      svg {
        font-size: 0.8em;
      }
    }

    &-content {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      grid-gap: $form-gap;
    }

    &-create-product {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius);
      background-color: hsla(var(--border-color-h), var(--border-color-s), var(--border-color-l), 0.2);
      color: hsla(var(--dark-h), var(--dark-s), var(--dark-l), 0.7);
      cursor: pointer;
      min-height: 280px;
    }

    & + & {
      margin-top: 5rem;
    }
  }
}
</style>
